import { useState } from "react";
import { submitInquiry } from "@/lib/cloudApi";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, ArrowRight } from "lucide-react";
import { BRAND, EVENT_TYPES } from "@/data/portfolio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: "", phone: "", email: "", wedding_date: "", wedding_city: "", venue: "", event_type: "", expected_budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please fill name, phone and email"); return; }
    setLoading(true);
    try {
      await submitInquiry(form);
      setSent(true); setForm(initialForm); toast.success("Inquiry sent successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  const inp = "form-field-line";

  return (
    <div data-testid="contact-page" className="bg-[#070707] text-[#F5EFE4] min-h-screen">

      {/* Hero */}
      <section className="pt-40 md:pt-52 pb-12 px-6 md:px-12" data-gsap="hero">
        <div className="max-w-[1380px] mx-auto">
          <span className="eyebrow text-[#B8914A] text-[0.6rem]" data-gsap="hero-kicker">Get In Touch</span>
          <h1 className="page-hero-title mt-3" data-gsap="hero-title">Let's talk.</h1>
          <p className="font-display-italic mt-6 text-[rgba(245,239,228,0.72)]"
            data-gsap="hero-subtitle"
            style={{ fontSize: "clamp(1.1rem,2vw,1.5rem)", maxWidth: "520px", lineHeight: 1.6 }}>
            Tell us about your day. We'll get back with a custom quotation crafted around your story.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-28 px-6 md:px-12" data-gsap="reveal">
        <div className="max-w-[1380px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14" data-gsap="stagger">

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-10" data-testid="contact-details" data-gsap="reveal">
            <div>
              <div className="eyebrow text-[#B8914A] text-[0.6rem] mb-5">Studio</div>
              <div className="font-display text-[1.7rem]">Mangalfera Films</div>
              <div className="text-[0.82rem] text-[rgba(245,239,228,0.55)] mt-2">{BRAND.city} · {BRAND.serviceArea}</div>
            </div>

            <div className="space-y-6 text-[0.83rem]">
              {[
                { icon: <Phone size={14} className="mt-[2px] text-[#B8914A] flex-shrink-0" />, label: "Phone / WhatsApp", val: `+91 ${BRAND.phone}`, href: `tel:+91${BRAND.phone}`, testid: "contact-phone" },
                { icon: <Mail size={14} className="mt-[2px] text-[#B8914A] flex-shrink-0" />, label: "Email", val: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email" },
                { icon: <MapPin size={14} className="mt-[2px] text-[#B8914A] flex-shrink-0" />, label: "Based In", val: BRAND.city },
                { icon: <Instagram size={14} className="mt-[2px] text-[#B8914A] flex-shrink-0" />, label: "Instagram", val: BRAND.instagramHandle, href: BRAND.instagram, testid: "contact-instagram" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  {item.icon}
                  <div>
                    <div className="eyebrow text-[0.55rem] text-[rgba(184,145,74,0.7)] mb-1">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined}
                        rel={item.href.startsWith("http") ? "noreferrer" : undefined}
                        className="text-[rgba(245,239,228,0.82)] hover:text-[#B8914A] transition-colors"
                        data-testid={item.testid}>{item.val}</a>
                    ) : (
                      <span className="text-[rgba(245,239,228,0.75)]">{item.val}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <a href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hello Mangalfera Films, I would like to enquire about wedding photography.")}`}
              target="_blank" rel="noreferrer"
              className="btn-gold-ghost inline-flex">
              Chat on WhatsApp
            </a>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8" data-gsap="reveal">
            {sent ? (
              <div className="border border-[rgba(184,145,74,0.25)] p-12 text-center">
                <div className="font-display text-[2rem] text-[#B8914A]">Thank You</div>
                <p className="mt-3 text-[0.83rem] text-[rgba(245,239,228,0.65)] leading-[1.85]">
                  Thank you for reaching out to Mangalfera Films.<br />Our team will contact you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  {[
                    { name: "name", label: "Name *", type: "text", placeholder: "Your full name", required: true },
                    { name: "phone", label: "Phone / WhatsApp *", type: "tel", placeholder: "+91 00000 00000", required: true },
                    { name: "email", label: "Email *", type: "email", placeholder: "you@example.com", required: true },
                    { name: "wedding_date", label: "Wedding Date", type: "date" },
                    { name: "wedding_city", label: "Wedding City", type: "text", placeholder: "Ahmedabad, Udaipur..." },
                    { name: "venue", label: "Venue", type: "text", placeholder: "Venue name (optional)" },
                    { name: "expected_budget", label: "Expected Budget", type: "text", placeholder: "Approx. budget range" },
                  ].map(f => (
                    <div key={f.name} className="mb-7">
                      <label className="eyebrow text-[0.55rem] text-[rgba(184,145,74,0.7)] block mb-1">{f.label}</label>
                      <input className={inp} type={f.type} name={f.name} placeholder={f.placeholder}
                        value={form[f.name]} onChange={onChange} required={f.required} />
                    </div>
                  ))}
                  <div className="mb-7">
                    <label className="eyebrow text-[0.55rem] text-[rgba(184,145,74,0.7)] block mb-1">Event Type</label>
                    <select className={inp} name="event_type" value={form.event_type} onChange={onChange}>
                      <option value="">Select event type</option>
                      {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-8">
                  <label className="eyebrow text-[0.55rem] text-[rgba(184,145,74,0.7)] block mb-1">Message</label>
                  <textarea className={inp} name="message" rows={5} placeholder="Tell us a bit about your wedding..."
                    value={form.message} onChange={onChange} style={{ resize: "vertical" }} />
                </div>
                <button type="submit" className="btn-gold-fill" disabled={loading}>
                  {loading ? "Sending..." : "Get Custom Quotation"} <ArrowRight size={13} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
